import React from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { nl } from 'date-fns/locale';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayState } from '../../utils/availability';
import { monthLabel } from '../../utils/format';
type Props = {
  month: Date;
  onMonthChange: (date: Date) => void;
  selected: string | null;
  onSelect: (iso: string) => void;
  stateFor: (date: Date) => DayState;
  minMonth?: Date;
};
const weekdays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
export function MonthCalendar({
  month,
  onMonthChange,
  selected,
  onSelect,
  stateFor,
  minMonth
}: Props) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), {
      weekStartsOn: 1
    }),
    end: endOfWeek(endOfMonth(month), {
      weekStartsOn: 1
    })
  });
  const canGoBack = !minMonth || startOfMonth(month) > startOfMonth(minMonth);
  return <div className="rounded-2xl border border-edge bg-carbon p-4 sm:p-5" data-id="element-392" data-mp="1hjs3lyftqhq8">
      <div className="flex items-center justify-between gap-3" data-id="element-393" data-mp="1hhfmtolsyder">
        <button type="button" onClick={() => onMonthChange(subMonths(month, 1))} disabled={!canGoBack} aria-label="Vorige maand" className="flex h-10 w-10 items-center justify-center rounded-full border border-edge text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember disabled:opacity-30" data-id="element-394" data-mp="1mhbupyxmeozl">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-395" data-mp="13ksrfq9hzvay" />
        </button>
        <span className="font-display text-lg font-extrabold capitalize tracking-tight text-chalk" data-id="element-396" data-mp="psw0vqnh9tgo">
          {monthLabel(month)}
        </span>
        <button type="button" onClick={() => onMonthChange(addMonths(month, 1))} aria-label="Volgende maand" className="flex h-10 w-10 items-center justify-center rounded-full border border-edge text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember" data-id="element-397" data-mp="tu7knfjo7kgy">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-398" data-mp="2hryw1x0d8h93" />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-[0.14em] text-dim" data-id="element-399" data-mp="2dq50u48xw5gb">
        {weekdays.map(d => <span key={d} className="py-1" data-id="element-400" data-mp="28sbqr04imrar">
            {d}
          </span>)}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1" data-id="element-401" data-mp="35g00ouufbdj8">
        {days.map(day => {
        const state = stateFor(day);
        const outside = !isSameMonth(day, month);
        const isSelected = selected ? isSameDay(day, new Date(`${selected}T00:00:00`)) : false;
        const disabled = outside || state.isPast || !state.open || state.free === 0;
        const today = isSameDay(day, new Date());
        return <button key={state.iso} type="button" disabled={disabled} aria-pressed={isSelected} aria-label={`${format(day, 'd MMMM', {
          locale: nl
        })}${disabled ? ' — niet beschikbaar' : ` — ${state.free} tijden vrij`}`} onClick={() => onSelect(state.iso)} className={`relative flex h-12 flex-col items-center justify-center rounded-xl border text-sm transition-colors duration-200 ease-smooth sm:h-14 ${isSelected ? 'border-ember bg-ember font-semibold text-void' : disabled ? 'border-transparent text-dim/35' : 'border-edge bg-panel text-chalk hover:border-ember/50'}`} data-id="element-402" data-mp="1kzd6zdfcjsw5">
              <span className={outside ? 'opacity-0' : ''} data-id="element-403" data-mp="23m6qyz7xcgxm">{format(day, 'd')}</span>
              {!outside && !disabled && !isSelected && <span className={`mt-1 h-1.5 w-1.5 rounded-full ${state.free > 2 ? 'bg-ember' : 'bg-ember/45'}`} aria-hidden="true" data-id="element-404" data-mp="4adynfw2zzr8" />}
              {today && !isSelected && !outside && <span className="absolute inset-x-3 bottom-1 h-px bg-chalk/25" aria-hidden="true" data-id="element-405" data-mp="1uqg8x9jdyvq5" />}
            </button>;
      })}
      </div>

      <p className="mt-4 flex flex-wrap items-center gap-4 text-xs text-dim" data-id="element-406" data-mp="23qmiikbyrbk1">
        <span className="flex items-center gap-2" data-id="element-407" data-mp="1g10ki07bv2dm">
          <span className="h-1.5 w-1.5 rounded-full bg-ember" aria-hidden="true" data-id="element-408" data-mp="1g1thi9ghidik" /> Ruim beschikbaar
        </span>
        <span className="flex items-center gap-2" data-id="element-409" data-mp="1c1jaqukvbj2n">
          <span className="h-1.5 w-1.5 rounded-full bg-ember/45" aria-hidden="true" data-id="element-410" data-mp="1prd2plro6jgl" /> Beperkt
        </span>
        <span className="flex items-center gap-2" data-id="element-411" data-mp="1kzbucaxmhcvu">
          <span className="h-1.5 w-1.5 rounded-full bg-dim/40" aria-hidden="true" data-id="element-412" data-mp="1kyjxyxw7bb4i" /> Vol of gesloten
        </span>
      </p>
    </div>;
}